import { useState } from "react";
import { useEffect } from "react";
import { OrderType } from "../../../types/order";
import { useAppState } from "../../../context/useAppState";
import { LeadTimePriceType } from "../../../types/common";
import { CountdownTimer } from "../../../components/CountdownTimer";
import WatchOnline from "./watch-online";
import { PRODUCT_TYPE } from "../../../constants";
import { fetchJson } from "../../../utils/fetch";
import { loadStripe } from "@stripe/stripe-js";

export const OrderRow = ({
    order,
    expanded,
    setExpanded
}: {
    order: OrderType;
    expanded: boolean;
    setExpanded: (expanded: boolean) => void;
}) => {
    const [leadTimePrices] = useAppState<LeadTimePriceType[]>('leadTimePrices', []);
    const [serverTime, setServerTime] = useState<number>(0);
    const [stripePublishableKey] = useAppState<string>('stripePublishableKey', '');
    const [isPaying, setIsPaying] = useState<boolean>(false);
    const [speedUpPayingItemId, setSpeedUpPayingItemId] = useState<number | null>(null);

    useEffect(() => {
        const syncServerTime = async () => {
            try {
                const response = await fetch('/api/time');
                const { time } = await response.json();
                setServerTime(time * 1000); // Convert to milliseconds
            } catch (error) {
                console.error('Failed to sync server time:', error);
            }
        };
        syncServerTime();
    }, []);

    const checkout = async (orderId: string) => {
        if (isPaying) return;
        setIsPaying(true);
        try {
            const { json: { sessionId } } = await fetchJson<{ sessionId: string }>(
                '/api/checkout/create-session',
                {
                    method: 'POST',
                    body: JSON.stringify({ orderId: parseInt(orderId) })
                }
            );

            if (sessionId && stripePublishableKey) {
                const stripe = await loadStripe(stripePublishableKey);
                if (stripe) {
                    const result = await stripe.redirectToCheckout({ sessionId });
                    if (result && result.error) {
                        console.error(result.error.message || 'Stripe redirect failed.');
                    }
                    return;
                } else {
                    console.error('Stripe failed to load. Please try again.');
                }
            } else {
                console.error('Failed to initiate payment session. Please try again.');
            }
        } catch (err) {
            console.error('Checkout failed:', err);
        } finally {
            // If redirect succeeds, the page will navigate away. Otherwise re-enable the button.
            setIsPaying(false);
        }
    };

    const speedUpCheckout = async (orderItemId: number, leadTimePriceId: number) => {
        if (speedUpPayingItemId !== null) return;
        setSpeedUpPayingItemId(orderItemId);
        try {
            const { json: { sessionId } } = await fetchJson<{ sessionId: string }>(
                '/api/checkout/speed-up-session',
                {
                    method: 'POST',
                    body: JSON.stringify({ orderItemId, leadTimePriceId }),
                }
            );

            if (sessionId && stripePublishableKey) {
                const stripe = await loadStripe(stripePublishableKey);
                if (stripe) {
                    const result = await stripe.redirectToCheckout({ sessionId });
                    if (result?.error) {
                        console.error(result.error.message || 'Stripe redirect failed.');
                    }
                    return;
                }
                console.error('Stripe failed to load. Please try again.');
            } else {
                console.error('Failed to initiate speed-up payment. Please try again.');
            }
        } catch (err) {
            console.error('Speed-up checkout failed:', err);
        } finally {
            setSpeedUpPayingItemId(null);
        }
    };

    return (
        <div className={`order order_expandable ${expanded ? 'm-active' : ''}`} 
        key={order.id}
        onClick={() => {
            if (!expanded) {
                setExpanded(true);
            }
        }}
        >
            <button className="order__tag order__tag_trigger" type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    setExpanded(!expanded);
                }}
            >
                Order #{order.id.toString().padStart(6, '0')}
            </button>
            <div className="order__label">
                View order details
            </div>

            <div className="order__preview-holder">
                <div className="order__preview xtable xtable_preview">
                    <div className="xtable__tr">
                        <div className="xtable__td">
                            {/* spacer */}
                        </div>
                        <div className="xtable__td">
                            {/* 
                                        place all previews here, 
                                        only four are visible, the rest are for css counter
                                        (no extra classes or scripts needed)
                                        */}
                            <div className="order__preview-pics">
                                {order.items.reduce((acc, item) => {
                                    acc.push(...item.preview.slice(0, 1));
                                    return acc;
                                }, [] as number[]).map((id) => (
                                    <picture className="order__preview-pic" key={`${id}-${order.id}`}>
                                        <source
                                            type="image/webp"
                                            srcSet={`/api/media/${id}?webp&s=s 1x, /api/media/${id}?webp&s=m 2x`}
                                        />
                                        <img
                                            className="order__preview-pic-img"
                                            src={`/api/media/${id}?s=s`}
                                            srcSet={`/api/media/${id}?s=m 2x`}
                                            loading="lazy"
                                        />
                                    </picture>
                                ))}

                            </div>
                        </div>

                        <div className="xtable__td">
                            {((o: OrderType) => {

                                const completed = o.items.every(i => (i.type === PRODUCT_TYPE.SIMPLE && i.status === 'delivered') || (i.type === PRODUCT_TYPE.CLIP && i.videoStory));
                                if (completed) {
                                    return (<div className="btn btn_lg btn_cta">
                                        Completed
                                    </div>)
                                }
                                if (!o.paid) {
                                    return (
                                        <button
                                            className={`btn btn_lg${isPaying ? ' is-disabled' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                checkout(o.id.toString());
                                            }}
                                            disabled={isPaying}
                                            aria-busy={isPaying}
                                            aria-disabled={isPaying}
                                            type="button"
                                        >
                                            {isPaying ? 'Processing…' : 'Pay now'}
                                        </button>
                                    )
                                }
                                return (<div className="btn btn_lg">
                                    In Process
                                </div>)
                            })(order)}
                        </div>
                        <div className="xtable__td">
                            <div className={`xtable__status ${order.paid ? 'm-paid' : 'm-pending'}`}>
                                {order.paid ? 'Paid' : 'Pending'}
                            </div>
                            <div className="order__preview-price">
                                ${order.totalCost.toFixed(2)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="order__holder">
                <div className="order__box xtable xtable_order">
                    {order.items.map((orderItem) => (
                        <div className="xtable__tr" key={orderItem.id}>
                            <div className="xtable__td">
                                <a href={`/catalog/product/${orderItem.productSlug}`} className="xtable__preview" onClick={(e) => e.stopPropagation()}>
                                    <picture className="xtable__preview-pic">
                                        <source
                                            type="image/webp"
                                            srcSet={`/api/media/${orderItem.preview[0]}?webp&s=s`}
                                        />
                                        <img
                                            className="xtable__preview-pic-img"
                                            src={`/api/media/${orderItem.preview[0]}?s=s`}
                                            alt={orderItem.name}
                                            loading="lazy"
                                        />
                                    </picture>
                                </a>
                            </div>

                            <div className="xtable__td">
                                <a href={`/catalog/product/${orderItem.productSlug}`} className="xtable__name" onClick={(e) => e.stopPropagation()}>{orderItem.name}</a>
                                <div className={`xtable__status ${order.paid ? 'm-paid' : 'm-pending'}`}>
                                    {order.paid ? 'Paid' : 'Need to pay'}
                                </div>
                            </div>

                            <div className="xtable__td">
                                {order.paid ? (
                                    orderItem.type === PRODUCT_TYPE.CLIP ? (
                                        orderItem.videoStory ? (
                                            <WatchOnline orderItem={orderItem} />
                                        ) : (
                                            <>
                                                <div className="order__control-label">Your video will be ready within:</div>
                                                {serverTime > 0 ? (
                                                    <CountdownTimer
                                                        startDate={orderItem.speedUpAt ?? order.paidAt}
                                                        className="order__control btn btn_cta btn_lg"
                                                        serverTime={serverTime}
                                                        processHours={leadTimePrices.find(p => p.id === orderItem.leadTimePrice)?.period}
                                                    />
                                                ) : (
                                                    <div className="order__control btn btn_cta btn_lg">
                                                        --:--:--
                                                    </div>
                                                )}
                                            </>
                                        )
                                    ) :
                                        <div className="order__control btn btn_lg">
                                            {(() => {
                                                if (orderItem.status === 'delivered') {
                                                    return 'Delivered';
                                                }
                                                if (orderItem.trackingNumber && orderItem.status === 'shipping') {
                                                    return 'Shipping';
                                                }
                                                return 'Packing';
                                            })()}
                                        </div>
                                ) : <div className="order__control btn btn_cta btn_lg">
                                   {leadTimePrices.find(p => p.id === orderItem.leadTimePrice)?.period}:00:00
                                </div>}

                            </div>

                            <div className="xtable__td">
                                {order.paid ?
                                    (orderItem.type === PRODUCT_TYPE.CLIP ? (
                                        (
                                            orderItem.videoStory
                                                ?
                                                <a href={`/video-story/${orderItem.id}`} className="order__control btn btn_cta btn_lg" onClick={(e) => e.stopPropagation()}>
                                                    <svg role="img" className="btn__ico">
                                                        <use href="./img/sprite.svg#download"></use>
                                                    </svg>
                                                    Download
                                                </a>
                                                :
                                                (() => {
                                                    const currentTier = leadTimePrices.find(p => p.id === orderItem.leadTimePrice);
                                                    const remainingTime = (order.paidAt && currentTier)
                                                        ? new Date(order.paidAt).getTime() + (currentTier.period * 3600000) - Date.now()
                                                        : 0;
                                                    const speedUpTiers = leadTimePrices.filter(
                                                        p => p.price > 0 && remainingTime > (p.period * 3600000)
                                                    );

                                                    if (currentTier?.price === 0 && !orderItem.speedUp && speedUpTiers.length > 0) {
                                                        return (
                                                            <>
                                                                <div className="order__control-label">Speed up your video creation:</div>
                                                                {speedUpTiers.map(p => (
                                                                    <button
                                                                        className={`order__control btn btn_alt btn_lg${speedUpPayingItemId === orderItem.id ? ' is-disabled' : ''}`}
                                                                        type="button"
                                                                        key={p.id}
                                                                        disabled={speedUpPayingItemId === orderItem.id}
                                                                        aria-busy={speedUpPayingItemId === orderItem.id}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            speedUpCheckout(orderItem.id, p.id);
                                                                        }}
                                                                    >
                                                                        {speedUpPayingItemId === orderItem.id ? 'Processing…' : p.caption}
                                                                        <span className="order__control-large-text">${p.price}</span>
                                                                    </button>
                                                                ))}
                                                            </>
                                                        );
                                                    }

                                                    return (
                                                        <>
                                                            <div className="order__control-label">Current status:</div>
                                                            <div className="btn btn_lg">Pending</div>
                                                        </>
                                                    );
                                                })()
                                        )
                                    ) : orderItem.status === 'delivered' ? (
                                        <button className="order__control btn btn_cta btn_lg" type="button" onClick={(e) => e.stopPropagation()}>
                                            {orderItem.trackingNumber}
                                        </button>
                                    ) : null) :
                                    <div className="btn btn_lg btn_alt">
                                        ${orderItem.price}
                                    </div>
                                }
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default OrderRow;